import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import TableChartIcon from '@mui/icons-material/TableChart'
import { adminOrderService, orderService } from '../../services'
import { getApiErrorMessage } from '../../services/apiClient'
import { formatOrderDate } from '../../utils/ordersListUtils'
import { formatOrganizationLabel } from '../../utils/organizationLabel'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { useOrganizationMultiSearch } from '../../hooks/useOrganizationMultiSearch'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />
const SEARCH_DEBOUNCE_MS = 450
const SEARCH_LIMIT = 25
const DEFAULT_RANGE_DAYS = 16
const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function toYmd(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function defaultDateRange() {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - (DEFAULT_RANGE_DAYS - 1))
  return { startDate: toYmd(start), endDate: toYmd(end) }
}

function formatPurchaseOrderOption(order) {
  if (!order) return ''
  const shopId = order.org_id != null ? order.org_id : '—'
  const shopName = order.organization_name || 'Unknown shop'
  return `PO #${order.order_id} · Shop ${shopId} · ${shopName}`
}

async function messageFromExportError(err, fallback) {
  return getApiErrorMessage(err, fallback)
}

function PurchaseOrderExportDialog({ open, onClose, onQueued }) {
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const [mode, setMode] = useState('date_range')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedOrders, setSelectedOrders] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [options, setOptions] = useState([])
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [selectedOrgIds, setSelectedOrgIds] = useState([])
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS)
  const {
    searchText: orgSearchText,
    setSearchText: setOrgSearchText,
    options: organizations,
    loading: orgsLoading,
    selectedOrgs,
    setSelectedFromOrgs,
    selectAllVisible,
    clearSelected: clearSelectedOrgs,
    resetSearch: resetOrgSearch,
  } = useOrganizationMultiSearch({ enabled: open })

  const dateError = useMemo(() => {
    if (!startDate || !endDate) return null
    if (!YMD_PATTERN.test(startDate) || !YMD_PATTERN.test(endDate)) {
      return 'Dates must use YYYY-MM-DD.'
    }
    if (startDate > endDate) {
      return 'Start date must be on or before end date.'
    }
    return null
  }, [startDate, endDate])

  const resetForm = useCallback(() => {
    const range = defaultDateRange()
    setMode('date_range')
    setStartDate(range.startDate)
    setEndDate(range.endDate)
    setSelectedOrders([])
    setSearchInput('')
    setOptions([])
    setSelectedOrgIds([])
    clearSelectedOrgs()
    resetOrgSearch()
    setError(null)
    setExporting(false)
  }, [clearSelectedOrgs, resetOrgSearch])

  useEffect(() => {
    if (open) resetForm()
  }, [open, resetForm])

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    ;(async () => {
      try {
        setOptionsLoading(true)
        const response = await orderService.getAllOrders({
          page: 1,
          limit: SEARCH_LIMIT,
          orderType: 'Purchase',
          ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        })
        if (!cancelled) {
          setOptions(response.data || response.items || [])
        }
      } catch (err) {
        console.error('Failed to search purchase orders', err)
        if (!cancelled) setOptions([])
      } finally {
        if (!cancelled) setOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, debouncedSearch])

  const handleClose = () => {
    if (exporting) return
    onClose()
  }

  const validate = () => {
    if (mode === 'date_range') {
      if (!startDate || !endDate) {
        setError('Select a start date and an end date.')
        return false
      }
      if (dateError) {
        setError(dateError)
        return false
      }
      return true
    }
    if (selectedOrders.length === 0) {
      setError('Select at least one purchase order.')
      return false
    }
    return true
  }

  const handleExport = async (format) => {
    if (exporting) return
    setError(null)
    if (!validate()) return

    try {
      setExporting(true)
      const payload =
        mode === 'date_range'
          ? {
              mode: 'date_range',
              start_date: startDate,
              end_date: endDate,
              ...(selectedOrgIds.length
                ? { org_ids: selectedOrgIds.map((id) => Number(id)) }
                : {}),
              format,
            }
          : {
              mode: 'ids',
              order_ids: selectedOrders.map((order) => Number(order.order_id)),
              format,
            }
      const response = await adminOrderService.exportPurchaseOrders(payload)
      onQueued?.(response)
      onClose()
    } catch (err) {
      console.error(`Error starting purchase-order export as ${format}:`, err)
      setError(
        messageFromExportError(
          err,
          `Failed to start ${format === 'csv' ? 'CSV' : 'Excel'} export`,
        ),
      )
    } finally {
      setExporting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isCompact}
      scroll="paper"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: '100%', sm: 600 },
          m: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pr: 2 }}>Export Purchase Orders</DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          overflowX: 'hidden',
        }}
      >
        <Stack spacing={2.5} sx={{ minWidth: 0, pt: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Export original vs modified quantities, prices, and who changed them.
            Order status matches the purchase-orders list. Each row also shows whether
            every article is delivered, the total delivered quantity of that article,
            how many shipments exist, how many are completed or still in transit, and
            the total pending article quantity. Each row also shows who created
            the order, the payment proof URL and date, and which admin approved the
            payment by starting procurement. Each shipment row shows the warehouse
            location code assigned when that shipment was created.
            Excel files include a Shipments sheet and a Legend sheet that explains every
            column. CSV files include those sections after the purchase-order table.
            The file is prepared in the background so you can keep using the portal.
            Superadmin only.
          </Typography>

          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ wordBreak: 'break-word' }}
            >
              {error}
            </Alert>
          )}

          {exporting && (
            <Alert severity="info" icon={<CircularProgress size={18} />}>
            Preparing export… You can close this dialog after it starts.
            </Alert>
          )}

          <FormControl component="fieldset" disabled={exporting} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Export criteria
            </Typography>
            <RadioGroup
              value={mode}
              onChange={(event) => {
                setMode(event.target.value)
                setError(null)
              }}
              sx={{ gap: 0.25 }}
            >
              <FormControlLabel
                value="date_range"
                control={<Radio size="small" />}
                label="Date range"
                sx={{ mr: 0 }}
              />
              <FormControlLabel
                value="ids"
                control={<Radio size="small" />}
                label="Selected purchase orders"
                sx={{ mr: 0 }}
              />
            </RadioGroup>
          </FormControl>

          {mode === 'date_range' ? (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: '#666', mb: 1.5 }}>
                Filters by created date (order_date), as Asia/Dhaka calendar days.
                This is the same field shown as Created Date in the export file.
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ minWidth: 0 }}
              >
                <TextField
                  label="Start date"
                  type="date"
                  size="small"
                  fullWidth
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(dateError)}
                  disabled={exporting}
                />
                <TextField
                  label="End date"
                  type="date"
                  size="small"
                  fullWidth
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(dateError)}
                  helperText={dateError || ' '}
                  disabled={exporting}
                />
              </Stack>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Shop filter (optional)
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 1.5 }}>
                Leave none selected to include every shop in the date range. When you pick one or
                more shops, only purchase orders for those Shop Ids are exported.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'flex-start',
                  minWidth: 0,
                }}
              >
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  loading={orgsLoading}
                  options={organizations}
                  filterOptions={(opts) => opts}
                  inputValue={orgSearchText}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setOrgSearchText(value || '')
                    }
                  }}
                  getOptionLabel={(o) => formatOrganizationLabel(o)}
                  isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
                  value={selectedOrgs}
                  onChange={(_, newValue) => {
                    setSelectedOrgIds(setSelectedFromOrgs(newValue))
                  }}
                  filterSelectedOptions={false}
                  disabled={exporting}
                  sx={{ flex: '1 1 260px', minWidth: 0 }}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...rest } = props
                    return (
                      <li key={key} {...rest}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {formatOrganizationLabel(option)}
                      </li>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Shop Id"
                      placeholder="Search shop name or Shop Id…"
                      helperText="Type a shop name or full Shop Id — results come from the server."
                    />
                  )}
                  componentsProps={{
                    popper: {
                      sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={exporting || !organizations.length}
                  onClick={() => setSelectedOrgIds(selectAllVisible())}
                  sx={{ color: '#e90000', borderColor: '#e90000', height: 40 }}
                >
                  Select all
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={exporting || selectedOrgIds.length === 0}
                  onClick={() => setSelectedOrgIds(clearSelectedOrgs())}
                  sx={{ height: 40 }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {selectedOrders.length} selected
                </Typography>
                <Button
                  size="small"
                  disabled={exporting || selectedOrders.length === 0}
                  onClick={() => setSelectedOrders([])}
                  sx={{ minWidth: 0 }}
                >
                  Clear all
                </Button>
              </Box>
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterSelectedOptions={false}
                filterOptions={(opts) => opts}
                options={options}
                loading={optionsLoading}
                value={selectedOrders}
                inputValue={searchInput}
                disabled={exporting}
                onChange={(_, value) => setSelectedOrders(value)}
                onInputChange={(_, value, reason) => {
                  if (reason !== 'reset') setSearchInput(value)
                }}
                isOptionEqualToValue={(a, b) => Number(a.order_id) === Number(b.order_id)}
                getOptionLabel={formatPurchaseOrderOption}
                renderOption={(props, option, { selected }) => {
                  const { key, ...rest } = props
                  return (
                    <li key={key} {...rest}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {formatPurchaseOrderOption(option)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Created {formatOrderDate(option.order_date)}
                        </Typography>
                      </Box>
                    </li>
                  )
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index })
                    return (
                      <Chip
                        key={key}
                        size="small"
                        {...tagProps}
                        label={`PO #${option.order_id}`}
                        sx={{ maxWidth: '100%' }}
                      />
                    )
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Search purchase orders"
                    placeholder="Order #, shop ID, or shop name…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {optionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={optionsLoading ? 'Searching…' : 'No purchase orders found'}
                ListboxProps={{
                  sx: { maxHeight: { xs: 220, sm: 280 } },
                }}
                componentsProps={{
                  popper: {
                    sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                  },
                  paper: {
                    sx: { maxWidth: 'calc(100vw - 24px)' },
                  },
                }}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  '& .MuiAutocomplete-inputRoot': { flexWrap: 'wrap' },
                }}
              />
              <FormHelperText>
                Search uses the same purchase-order listing API. You can select
                multiple orders across searches.
              </FormHelperText>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
        }}
      >
        <Button
          onClick={handleClose}
          disabled={exporting}
          sx={{ width: { xs: '100%', sm: 'auto' }, mr: { sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          startIcon={exporting ? <CircularProgress size={16} /> : <TableChartIcon />}
          disabled={exporting}
          onClick={() => handleExport('csv')}
          sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' }, minWidth: 0 }}
        >
          {isCompact ? 'CSV' : 'Start CSV export'}
        </Button>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          disabled={exporting}
          onClick={() => handleExport('xlsx')}
          sx={{
            bgcolor: '#e90000',
            '&:hover': { bgcolor: '#c00000' },
            flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
            minWidth: 0,
          }}
        >
          {isCompact ? 'Excel' : 'Start Excel export'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PurchaseOrderExportDialog
